import React, { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import {
  Heart, MapPin, Calendar, ArrowRight, Lock, CalendarClock,
  CheckCircle2, Circle, AlertCircle, Bell, Sparkles, Users, Handshake, Camera, Wallet, Music, Bot, Radio,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import MyEventSidebar from "./MyEventSidebar";

const TECHNICAL_RE = /^(test[_\s-]|iter\d+|fixture|demo[_\s-]|debug|__)/i;
const isTech = (s) => !s || TECHNICAL_RE.test(String(s).trim());
function cleanEventName(raw) {
  if (!raw) return "";
  let s = String(raw).replace(/^\s*(test[_\s-]+|demo[_\s-]+)/i, "").trim().replace(/\s+[a-f0-9]{6,}$/i, "").trim();
  return isTech(s) ? "" : s;
}
function coupleName(e, o) {
  const c = cleanEventName(e?.name); if (c) return c;
  const a = !isTech(o?.prenom) ? (o?.prenom || "") : "";
  const b = !isTech(o?.partenaire_prenom) ? (o?.partenaire_prenom || "") : "";
  if (a && b) return `${a} & ${b}`; if (a) return a; return "Votre mariage";
}
function fmtDate(iso) { if (!iso) return "Date à préciser"; try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); } catch { return iso; } }

const ICONS = { calendar: CalendarClock, users: Users, handshake: Handshake, camera: Camera, wallet: Wallet, music: Music, bot: Bot, radio: Radio };

function CircularProgress({ percent }) {
  const R = 46, C = 2 * Math.PI * R, off = C - (Math.max(0, Math.min(100, percent)) / 100) * C;
  return (
    <div className="relative flex items-center justify-center" data-testid="myevent-progress-circle">
      <svg viewBox="0 0 120 120" className="w-32 h-32 -rotate-90">
        <circle cx="60" cy="60" r={R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="4" />
        <circle cx="60" cy="60" r={R} fill="none" stroke="#DCB457" strokeWidth="4"
          strokeDasharray={C} strokeDashoffset={off} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 800ms ease" }} />
      </svg>
      <div className="absolute text-center">
        <p className="font-serif-luxe text-3xl gold-text">{percent}<span className="text-lg">%</span></p>
        <p className="text-[9px] uppercase tracking-widest text-white/50 mt-0.5">Organisé</p>
      </div>
    </div>
  );
}

export default function MyEventDashboard() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [pl, setPl] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!eventId) return;
    api.get(`/next/myevent/${eventId}/dashboard`)
      .then(r => setD(r.data))
      .catch(e => setErr(e?.response?.status || 500));
    api.get(`/next/myevent/${eventId}/planning`)
      .then(r => setPl(r.data))
      .catch(() => setPl({ items: [] }));
  }, [eventId]);

  if (!user) return <Navigate to="/connexion" replace />;
  if (err === 403) return <FullError title="Accès refusé" text="Vous n'êtes pas membre de cet événement." testid="myevent-forbidden" />;
  if (err === 404) return <FullError title="Événement introuvable" text="Cet événement n'existe pas." testid="myevent-notfound" />;
  if (err) return <FullError title="Erreur" text="Impossible de charger votre espace." testid="myevent-error" />;
  if (!d) return <div className="min-h-screen bg-black text-white p-8"><p className="text-white/50 italic">Chargement de votre espace…</p></div>;

  const { event, organizer, subscription, plan, has_active_plan, modules, progress, next_rdv, unread_notifications } = d;
  const name = coupleName(event, organizer);
  const planningItems = (pl?.items || []).slice(0, 4);

  return (
    <div className="min-h-screen bg-black text-white pb-20" data-testid="myevent-dashboard">
      <header className="border-b border-[#DCB457]/15 bg-black/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-serif-luxe text-lg md:text-xl gold-text">My Animation</Link>
            <span className="inline-flex items-center gap-1.5 text-[9px] md:text-[10px] uppercase tracking-[0.24em] text-[#DCB457] border border-[#DCB457]/40 px-2 py-1">
              <Heart size={11} strokeWidth={1.5} /> MyEvent
            </span>
          </div>
          <div className="flex items-center gap-4 text-[11px] uppercase tracking-[0.22em] text-white/55">
            {unread_notifications > 0 && (
              <span className="hidden sm:inline-flex items-center gap-1.5 text-[#DCB457]" data-testid="myevent-unread-count">
                <Bell size={12} /> {unread_notifications}
              </span>
            )}
            <span className="hidden md:inline text-[10px] text-white/40 truncate max-w-[200px]">{name}</span>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-8 lg:pt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <MyEventSidebar />

        <main className="lg:col-span-9 min-w-0 space-y-8">
          {/* Header couple */}
          <section data-testid="myevent-header">
            <p className="eyebrow">MyEvent · Votre dossier</p>
            <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3 leading-tight" data-testid="myevent-couple-name">
              Bonjour <span className="italic gold-text">{name}</span> <Heart size={22} className="inline-block text-[#DCB457] mb-1" strokeWidth={1.5} />
            </h1>
            <p className="mt-3 text-white/60 text-sm md:text-base">
              Mariage {event.date && <>· <span data-testid="myevent-date">{fmtDate(event.date)}</span></>} {event.lieu && <>· {event.lieu}</>}
            </p>
          </section>

          {/* Hero premium immersif : Le grand jour */}
          <section
            className="relative overflow-hidden border border-[#DCB457]/25 rounded-sm"
            data-testid="myevent-hero"
            style={{ minHeight: "460px" }}
          >
            {/* Photo mariage stylisée en arrière-plan (élément décoratif, aucune donnée client) */}
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: "url('https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1600&q=70')",
                filter: "saturate(0.85)",
              }}
              aria-hidden
            />
            {/* Overlays sombres pour lisibilité */}
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" aria-hidden />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" aria-hidden />
            <div className="absolute -top-6 -right-6 opacity-[0.05] pointer-events-none">
              <Heart size={320} strokeWidth={0.4} className="text-[#DCB457]" />
            </div>

            <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-8 items-center p-8 md:p-12">
              <div className="md:col-span-2">
                <p className="eyebrow flex items-center gap-2 text-[#DCB457]">
                  <Calendar size={11} /> Le grand jour
                </p>
                {event.j_minus !== null && event.j_minus !== undefined && (
                  <p
                    className="mt-3 font-serif-luxe text-6xl md:text-7xl leading-none gold-text"
                    data-testid="myevent-countdown"
                  >
                    {event.j_minus > 0 ? `J − ${event.j_minus}` : event.j_minus === 0 ? "C'est aujourd'hui" : `J + ${Math.abs(event.j_minus)}`}
                  </p>
                )}
                <p className="font-serif-luxe text-2xl md:text-3xl mt-4 uppercase tracking-wide text-white">
                  {fmtDate(event.date)}
                </p>
                {(event.lieu || event.ville) && (
                  <p className="mt-3 text-white/80 text-sm md:text-base flex items-start gap-2">
                    <MapPin size={15} className="text-[#DCB457] mt-0.5 flex-shrink-0" />
                    <span>
                      {event.lieu && <span>{event.lieu}</span>}
                      {event.ville && <span className="block text-white/60 text-xs mt-0.5">{event.code_postal || ""} {event.ville}</span>}
                    </span>
                  </p>
                )}
                <p className="mt-8 max-w-lg font-serif-luxe italic text-white/75 text-base md:text-lg leading-relaxed">
                  « Le plus beau jour de votre vie mérite une organisation à votre image. »
                </p>
                <Link
                  to={`/myevent/${eventId}/planning`}
                  data-testid="myevent-hero-cta"
                  className="mt-6 inline-flex items-center gap-2 border border-[#DCB457]/60 text-[#DCB457] hover:bg-[#DCB457] hover:text-black px-5 py-2.5 text-xs uppercase tracking-widest transition"
                >
                  Voir mon événement <ArrowRight size={12} />
                </Link>
              </div>
              <div className="flex flex-col items-center justify-center md:border-l md:border-[#DCB457]/20 md:pl-8">
                <p className="eyebrow mb-3 text-[#DCB457]">Avancement</p>
                <CircularProgress percent={progress.percent} />
                <p className="text-[10px] uppercase tracking-widest text-white/50 mt-3">{progress.done}/{progress.total} étapes</p>
              </div>
            </div>
          </section>

          {!has_active_plan && (
            <section className="card-luxe p-6 border-[#DCB457]/40" data-testid="myevent-no-plan-banner">
              <p className="eyebrow flex items-center gap-2"><Sparkles size={11} /> MyEvent en préparation</p>
              <h2 className="font-serif-luxe text-2xl mt-2">{subscription?.status === "pending" ? "Paiement en cours" : "Choisissez votre forfait"}</h2>
              <p className="mt-2 text-white/65 text-sm">
                {subscription?.status === "pending"
                  ? "Nous attendons la confirmation de votre paiement."
                  : "Aucun forfait n'est encore actif. Votre organisateur My Animation vous transmettra le lien de souscription."}
              </p>
            </section>
          )}

          {/* Actions importantes */}
          <section data-testid="myevent-actions">
            <div className="flex items-baseline justify-between mb-4">
              <p className="eyebrow">Vos actions importantes</p>
            </div>
            <div className="card-luxe divide-y divide-[#DCB457]/10">
              {progress.steps.filter(s => !s.done).length === 0 ? (
                <p className="p-6 text-white/50 italic text-sm">Tout est à jour. Félicitations !</p>
              ) : (
                progress.steps.filter(s => !s.done).slice(0, 5).map(s => (
                  <div key={s.key} className="flex items-center justify-between px-5 py-4">
                    <div className="flex items-center gap-3">
                      <AlertCircle size={14} className="text-amber-300/80" strokeWidth={1.5} />
                      <p className="text-sm text-white/80">{s.label}</p>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-white/40">À faire</span>
                  </div>
                ))
              )}
            </div>
          </section>

          {/* Rangée : prochain RDV + notifications + forfait */}
          <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Kpi title="Prochain RDV" testid="kpi-rdv"
              value={next_rdv ? (next_rdv.title || "RDV programmé") : "Aucun RDV"}
              sub={next_rdv?.start_at ? new Date(next_rdv.start_at).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" }) : "À venir"}
              href={next_rdv ? "/espace-client/rdv" : null} />
            <Kpi title="Notifications" testid="kpi-notifs"
              value={unread_notifications > 0 ? `${unread_notifications} non lue${unread_notifications > 1 ? "s" : ""}` : "À jour"}
              sub={unread_notifications > 0 ? "Cliquez pour consulter" : "Aucun message"}
              href={unread_notifications > 0 ? "/espace-client/notifications" : null} />
            <Kpi title="Votre forfait" testid="kpi-plan"
              value={plan?.name || "Aucun"}
              sub={has_active_plan ? "Actif" : (subscription?.status === "pending" ? "Paiement en cours" : "À souscrire")}
              accent={has_active_plan} />
          </section>

          {/* MyPlanning preview */}
          <section data-testid="myevent-planning-preview">
            <div className="flex items-baseline justify-between mb-4">
              <p className="eyebrow flex items-center gap-2"><CalendarClock size={11} /> Mon planning</p>
              <Link to={`/myevent/${eventId}/planning`} data-testid="planning-see-all" className="text-[10px] uppercase tracking-widest text-[#DCB457] hover:underline inline-flex items-center gap-1">
                Voir tout <ArrowRight size={11} />
              </Link>
            </div>
            <div className="card-luxe divide-y divide-[#DCB457]/10">
              {planningItems.length === 0 ? (
                <p className="p-6 text-white/50 italic text-sm">Aucune étape planifiée. <Link to={`/myevent/${eventId}/planning`} className="text-[#DCB457] hover:underline">Ajouter des étapes</Link></p>
              ) : (
                planningItems.map((it, i) => (
                  <div key={i} className="flex items-center gap-4 px-5 py-3">
                    <span className="font-serif-luxe text-[#DCB457] text-sm w-16">{it.heure || "—"}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white/85 truncate">{it.label || "—"}</p>
                      {it.notes && <p className="text-[10px] uppercase tracking-widest text-white/40 truncate">{it.notes}</p>}
                    </div>
                    <CheckCircle2 size={14} className="text-[#DCB457]/40" strokeWidth={1.4} />
                  </div>
                ))
              )}
            </div>
          </section>

          {/* Modules */}
          <section>
            <p className="eyebrow mb-4">Vos modules MyEvent</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-testid="myevent-modules">
              {modules.map(m => <ModuleCard key={m.key} m={m} />)}
            </div>
          </section>

          {/* Signature émotionnelle */}
          <section className="pt-10 pb-4 text-center border-t border-[#DCB457]/10 mt-6" data-testid="myevent-signature">
            <Sparkles size={18} className="mx-auto text-[#DCB457]/70 mb-4" strokeWidth={1.2} />
            <p className="font-serif-luxe italic text-xl md:text-2xl text-white/80 leading-relaxed max-w-lg mx-auto">
              Créons ensemble des souvenirs<br />
              <span className="gold-text">qui dureront toute une vie.</span>
            </p>
          </section>

          <p className="text-[10px] uppercase tracking-widest text-white/25 text-center pt-8">
            MyAnimation NEXT · MyEvent v2
          </p>
        </main>
      </div>
    </div>
  );
}

function Kpi({ title, value, sub, href, accent, testid }) {
  const content = (
    <div className={`card-luxe p-5 ${href ? "hover:border-[#DCB457] transition" : ""}`} data-testid={testid}>
      <p className="eyebrow">{title}</p>
      <p className={`font-serif-luxe text-xl mt-3 ${accent ? "gold-text" : ""}`}>{value}</p>
      <p className="text-[10px] uppercase tracking-widest text-white/45 mt-1">{sub}</p>
    </div>
  );
  return href ? <Link to={href}>{content}</Link> : content;
}

function ModuleCard({ m }) {
  const Icon = ICONS[m.icon] || CalendarClock;
  const active = m.status === "active";
  const coming = m.status === "coming";
  const locked = m.status === "locked" || m.status === "no_plan";
  const testid = `module-${m.key.toLowerCase()}`;
  const inner = (
    <div className={`group card-luxe p-4 min-h-[120px] flex flex-col justify-between ${active ? "hover:border-[#DCB457]" : "opacity-60"}`}>
      <div className="flex items-start justify-between">
        <Icon size={20} strokeWidth={1.3} className={active ? "text-[#DCB457]" : "text-white/40"} />
        {active && <ArrowRight size={12} className="text-[#DCB457] opacity-0 group-hover:opacity-100 transition" />}
        {locked && <Lock size={12} className="text-white/25" />}
      </div>
      <div>
        <p className={`font-serif-luxe text-base ${active ? "" : "text-white/60"}`}>{m.label}</p>
        <p className="text-[9px] uppercase tracking-[0.2em] mt-1">
          {active && <span className="text-[#DCB457]">Ouvrir</span>}
          {coming && <span className="text-white/40">Bientôt disponible</span>}
          {m.status === "locked" && <span className="text-white/40">Non inclus</span>}
          {m.status === "no_plan" && <span className="text-white/40">Forfait requis</span>}
        </p>
      </div>
    </div>
  );
  if (active && m.legacy_href) return <Link to={m.legacy_href} data-testid={testid}>{inner}</Link>;
  return <div data-testid={testid}>{inner}</div>;
}

function FullError({ title, text, testid }) {
  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-8">
      <div className="max-w-md text-center" data-testid={testid}>
        <p className="eyebrow">MyEvent</p>
        <h1 className="font-serif-luxe text-3xl mt-3 mb-3">{title.split(" ")[0]}{" "}<span className="italic gold-text">{title.split(" ").slice(1).join(" ") || "."}</span></h1>
        <p className="text-white/60 text-sm">{text}</p>
        <Link to="/" className="mt-6 inline-block text-[#DCB457] text-xs uppercase tracking-widest hover:underline">Retour à l'accueil</Link>
      </div>
    </div>
  );
}
